/**
 * ActionDialogQueue
 *
 * Mounted once at the app root. Subscribes to NotificationContext for
 * pending-action notifications (kind === 'action_required' | 'failure')
 * and surfaces them as soft-blocking modals.
 *
 * Behaviour
 *   • ≤ 2 pending  → pager-style modal (1 card with Next →)
 *   • ≥ 3 pending  → inbox-style modal (scrollable list)
 *   • Esc / outside click  → snooze 10 min (re-pops automatically)
 *   • Primary CTA  → POST /action-taken + navigate to deep link
 *   • Dismiss      → POST /dismiss-action (won't re-pop unless re-fired)
 *
 * Renders nothing when the queue is empty.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sparkles, FileText, Image as ImageIcon, CheckCircle2,
  AlertCircle, AlertTriangle, ChevronRight, ChevronLeft,
  Clock, ExternalLink, X,
} from 'lucide-react';
import { useNotificationContext } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import useIsInFocusedFlow from '../hooks/useIsInFocusedFlow';
import api from '../api/client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

// ────────────────────────────────────────────────────────────────────
// Display helpers
// ────────────────────────────────────────────────────────────────────

/** Per-type visual config: icon component, tint colors, primary CTA label. */
const TYPE_CONFIG = {
  video_stage_complete: {
    accent: 'orange',
    icon: FileText,
    primaryLabel: 'Review & Approve',
  },
  video_stage_failed: {
    accent: 'red',
    icon: AlertCircle,
    primaryLabel: 'Open block',
    extraLabel: 'Retry stage',
  },
  batch_complete: {
    accent: 'emerald',
    icon: CheckCircle2,
    primaryLabel: 'Open course',
  },
  batch_partial_fail: {
    accent: 'amber',
    icon: AlertTriangle,
    primaryLabel: 'Review failed',
  },
  block_gen_failed: {
    accent: 'red',
    icon: AlertCircle,
    primaryLabel: 'Open block',
    extraLabel: 'Retry',
  },
  block_gen_complete: {
    accent: 'emerald',
    icon: CheckCircle2,
    primaryLabel: 'Open block',
  },
  default: {
    accent: 'slate',
    icon: Sparkles,
    primaryLabel: 'Open',
  },
};

const ACCENT_CLASSES = {
  orange: {
    border: 'border-orange-300',
    headerBg: 'bg-gradient-to-br from-orange-50 to-amber-50',
    iconBg: 'bg-orange-100 text-orange-700',
    pill: 'bg-orange-100 text-orange-800 border-orange-300',
    primaryBtn: 'bg-orange-600 hover:bg-orange-700 text-white',
  },
  emerald: {
    border: 'border-emerald-300',
    headerBg: 'bg-gradient-to-br from-emerald-50 to-teal-50',
    iconBg: 'bg-emerald-100 text-emerald-700',
    pill: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    primaryBtn: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  },
  amber: {
    border: 'border-amber-300',
    headerBg: 'bg-gradient-to-br from-amber-50 to-yellow-50',
    iconBg: 'bg-amber-100 text-amber-700',
    pill: 'bg-amber-100 text-amber-800 border-amber-300',
    primaryBtn: 'bg-amber-600 hover:bg-amber-700 text-white',
  },
  red: {
    border: 'border-rose-300',
    headerBg: 'bg-gradient-to-br from-rose-50 to-red-50',
    iconBg: 'bg-rose-100 text-rose-700',
    pill: 'bg-rose-100 text-rose-800 border-rose-300',
    primaryBtn: 'bg-rose-600 hover:bg-rose-700 text-white',
  },
  slate: {
    border: 'border-slate-300',
    headerBg: 'bg-slate-50',
    iconBg: 'bg-slate-100 text-slate-700',
    pill: 'bg-slate-100 text-slate-800 border-slate-300',
    primaryBtn: 'bg-slate-800 hover:bg-slate-900 text-white',
  },
};

function getConfig(notification) {
  return TYPE_CONFIG[notification?.type] || TYPE_CONFIG.default;
}

function relativeTime(iso) {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch {
    return '';
  }
}

// ────────────────────────────────────────────────────────────────────
// Card body (used by both pager + inbox variants)
// ────────────────────────────────────────────────────────────────────

function NotificationCard({ notification, onTakeAction, onDismiss, compact = false }) {
  const meta = notification.metadata || {};
  const cfg = getConfig(notification);
  const accent = ACCENT_CLASSES[cfg.accent] || ACCENT_CLASSES.slate;
  const Icon = cfg.icon;

  const courseTitle = meta.courseTitle;
  const topicTitle = meta.topicTitle;
  const blockTitle = meta.blockTitle;
  const stage = meta.stage;
  const succeededCount = meta.succeededCount;
  const failedCount = meta.failedCount;
  const totalBlocks = meta.totalBlocks;
  const errMsg = meta.error;

  return (
    <div
      data-testid={`action-card-${notification.id}`}
      className={`rounded-lg border-2 ${accent.border} overflow-hidden bg-white shadow-sm`}
    >
      {/* Header */}
      <div className={`${accent.headerBg} px-5 py-4 flex items-start gap-3`}>
        <div className={`${accent.iconBg} w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-slate-900">
              {notification.title}
            </h3>
            {stage && (
              <Badge variant="outline" className={`${accent.pill} text-xs px-2 py-0`}>
                Stage {stage}
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {relativeTime(notification.createdAt)}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4 space-y-3">
        {/* Context lines */}
        {(courseTitle || topicTitle || blockTitle) && (
          <div className="text-sm space-y-1">
            {courseTitle && (
              <div className="flex gap-2">
                <span className="text-slate-500 w-14 flex-shrink-0">Course</span>
                <span className="font-medium text-slate-800 truncate">{courseTitle}</span>
              </div>
            )}
            {topicTitle && (
              <div className="flex gap-2">
                <span className="text-slate-500 w-14 flex-shrink-0">Topic</span>
                <span className="font-medium text-slate-800 truncate">{topicTitle}</span>
              </div>
            )}
            {blockTitle && (
              <div className="flex gap-2">
                <span className="text-slate-500 w-14 flex-shrink-0">Block</span>
                <span className="font-medium text-slate-800 truncate">{blockTitle}</span>
              </div>
            )}
          </div>
        )}

        {/* Batch-specific summary */}
        {totalBlocks != null && (
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="bg-slate-50 rounded p-2">
              <div className="text-lg font-semibold text-slate-900">{totalBlocks}</div>
              <div className="text-slate-500">Total</div>
            </div>
            <div className="bg-emerald-50 rounded p-2">
              <div className="text-lg font-semibold text-emerald-700">{succeededCount ?? 0}</div>
              <div className="text-emerald-600">Succeeded</div>
            </div>
            <div className="bg-rose-50 rounded p-2">
              <div className="text-lg font-semibold text-rose-700">{failedCount ?? 0}</div>
              <div className="text-rose-600">Failed</div>
            </div>
          </div>
        )}

        {/* Free-text message */}
        {notification.message && !compact && (
          <p className="text-sm text-slate-700 leading-relaxed">
            {notification.message}
          </p>
        )}

        {/* Error block */}
        {errMsg && (
          <div className="bg-rose-50 border border-rose-200 rounded px-3 py-2 text-xs text-rose-800 font-mono">
            {errMsg}
          </div>
        )}
      </div>

      {/* Footer CTAs */}
      <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-2">
        <button
          onClick={() => onDismiss(notification)}
          className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1 transition-colors"
          data-testid={`action-dismiss-${notification.id}`}
        >
          Dismiss
        </button>
        <Button
          onClick={() => onTakeAction(notification)}
          className={`${accent.primaryBtn} h-9`}
          data-testid={`action-primary-${notification.id}`}
        >
          {cfg.primaryLabel}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Inbox-style list row (used when ≥ 3 pending)
// ────────────────────────────────────────────────────────────────────

function InboxRow({ notification, onTakeAction, onDismiss }) {
  const meta = notification.metadata || {};
  const cfg = getConfig(notification);
  const accent = ACCENT_CLASSES[cfg.accent] || ACCENT_CLASSES.slate;
  const Icon = cfg.icon;

  return (
    <div className="flex items-start gap-3 px-4 py-3 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors">
      <div className={`${accent.iconBg} w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{notification.title}</div>
        <div className="text-xs text-slate-500 truncate">
          {meta.blockTitle || meta.courseTitle || notification.message}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5">
          {relativeTime(notification.createdAt)}
        </div>
      </div>
      <div className="flex flex-col gap-1 items-end">
        <Button
          size="sm"
          onClick={() => onTakeAction(notification)}
          className={`${accent.primaryBtn} h-7 px-2 text-xs`}
          data-testid={`inbox-action-${notification.id}`}
        >
          {cfg.primaryLabel}
          <ExternalLink className="w-3 h-3 ml-1" />
        </Button>
        <button
          onClick={() => onDismiss(notification)}
          className="text-[10px] text-slate-400 hover:text-slate-700"
          data-testid={`inbox-dismiss-${notification.id}`}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────

// "Don't show until next login": the flag is cleared on login and logout (AuthContext, AuthCallback).
export const HIDE_UNTIL_LOGIN_KEY = 'actionDialogHiddenUntilLogin';

const HideUntilLoginButton = ({ onClick }) => (
  <button type="button" onClick={onClick} className="text-[11px] text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline" data-testid="action-dialog-hide-until-login">
    Don't show until next login
  </button>
);

export default function ActionDialogQueue() {
  const {
    pendingActions,
    snoozeActionNotification,
    takeActionOnNotification,
    dismissActionNotification,
    // 2026-02-24 — Manual queue override so the user can pull the
    // review dialog up on demand from the bell even inside a lab route.
    manualQueueOpen,
    closePendingActionsQueue,
  } = useNotificationContext();
  const { user, switchOrganization } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pagerIdx, setPagerIdx] = useState(0);
  const [hiddenUntilLogin, setHiddenUntilLogin] = useState(() => localStorage.getItem(HIDE_UNTIL_LOGIN_KEY) === '1');
  const hideUntilLogin = () => {
    localStorage.setItem(HIDE_UNTIL_LOGIN_KEY, '1');
    setHiddenUntilLogin(true);
    closePendingActionsQueue();
  };

  // Reset pager when queue mutates (new event pushes pager back to 0).
  useEffect(() => {
    if (pagerIdx >= pendingActions.length) setPagerIdx(0);
  }, [pendingActions.length, pagerIdx]);

  // 2026-07-09 UX fix — "second dialog popping ON TOP of Course Builder".
  // -------------------------------------------------------------------
  // Repro: user has 5+ video blocks with Stage 1 complete. The queue
  // pops dialog #1 → user clicks Review & Approve → we navigate to
  // /courses/{id}/edit?focusBlock={blockId}&action=review AND pop
  // notification #1 off `pendingActions`. But `pendingActions` still
  // has 4 more `action_required` notifications, so `open` stays true
  // and dialog #2 immediately renders on top of the Course Builder
  // the user is now trying to actually work on. Feels like the app
  // is nagging them mid-task.
  //
  // Fix: while the URL identifies a specific block-review context
  // (either the dialog just deep-linked us here, or the user manually
  // opened one), completely suppress the queue's dialog. The user is
  // actively working on ONE thing; the other pending reviews are
  // still in the pending pill / notification tray to come back to.
  // The moment the user navigates away from the focused-block URL
  // (Back to Courses, sidebar navigation, etc.), the queue re-renders
  // the next actionable item automatically — no data was lost, only
  // deferred.
  //
  // 2026-02-24 — Consolidated route matching into the shared
  // `useIsInFocusedFlow` hook so the same rules apply to
  // <GlobalJobsPill /> and <GenerationToastBridge />. See that hook
  // for the current list of focused-work routes.
  const isFocusedBlockReview = useIsInFocusedFlow();

  // Open policy:
  //   • Auto-open when there are pending items AND user is NOT in a
  //     focused-work route (labs / immersive player / previews).
  //   • Manual override — user clicked "Review pending actions" from
  //     the bell dropdown — bypass the route suppression entirely.
  //     Cleared by handleOpenChange / handleTakeAction / handleDismiss.
  const open = pendingActions.length > 0
    && (manualQueueOpen || (!hiddenUntilLogin && !isFocusedBlockReview));

  // Soft-block: Esc / outside-click → snooze the topmost item 10 min.
  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && pendingActions.length > 0) {
      const target = pendingActions[pagerIdx] || pendingActions[0];
      if (target) snoozeActionNotification(target.id, 10);
    }
    // Always clear the manual override when the dialog closes so the
    // route-suppression rule takes over again on the next opening.
    if (!nextOpen) closePendingActionsQueue();
  };

  /**
   * If the notification targets a course in a different org than the
   * user's current `orgId`, switch the org context FIRST so the
   * Course Builder / View page's `user.orgId`-based API calls hit the
   * right tenant. Without this, SuperAdmins clicking a deep-link for
   * a cross-tenant course land on a 404'd page.
   */
  const ensureOrgContext = async (targetOrgId) => {
    if (!targetOrgId || targetOrgId === user?.orgId) return;
    try {
      const res = await api.get(`/organizations/${targetOrgId}`);
      if (res?.data?.id) {
        switchOrganization(res.data);
      }
    } catch (e) {
      // Best-effort — if we can't fetch the org (no permission, etc.),
      // navigation will still attempt. The destination page will surface
      // its own 404 / permission error.
      console.warn('[ActionDialog] Could not switch org before nav:', e);
    }
  };

  const handleTakeAction = async (notification) => {
    // (see 2026-02 comment above for the multi-click snooze rationale)
    const others = pendingActions.filter((n) => n.id !== notification.id);

    takeActionOnNotification(notification.id);
    // Clear manual override — the user acted, subsequent renders should
    // respect the normal route-based suppression.
    closePendingActionsQueue();

    // Fire snoozes in parallel (state updates auto-batch; server-side
    // POSTs are fire-and-forget from our POV since we've already
    // optimistically closed the dialog via navigation below).
    others.forEach((n) => snoozeActionNotification(n.id, 30));

    const targetOrgId = notification?.metadata?.targetOrgId;
    if (targetOrgId) {
      await ensureOrgContext(targetOrgId);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleDismiss = (notification) => {
    dismissActionNotification(notification.id);
    closePendingActionsQueue();
  };

  const useInboxView = pendingActions.length >= 3;
  const currentPagerItem = pendingActions[pagerIdx] || pendingActions[0];

  // Memoised count for footer hint
  const remainingCount = useMemo(
    () => Math.max(0, pendingActions.length - 1 - pagerIdx),
    [pendingActions.length, pagerIdx],
  );

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden"
        data-testid="action-dialog-queue"
      >
        {useInboxView ? (
          <>
            <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200">
              <DialogTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-orange-600" />
                {pendingActions.length} actions need your attention
              </DialogTitle>
              <DialogDescription className="text-xs">
                Stage completions, batch results &amp; failures from your generations.
                <span className="block mt-1 text-[11px] text-slate-500">
                  Pick one to open &mdash; the others auto-snooze for 30&nbsp;min so you can focus.
                </span>
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-4 space-y-2 bg-slate-50">
              {pendingActions.map(n => (
                <InboxRow
                  key={n.id}
                  notification={n}
                  onTakeAction={handleTakeAction}
                  onDismiss={handleDismiss}
                />
              ))}
            </div>
            <div className="px-6 py-3 bg-white border-t border-slate-200 flex justify-between items-center">
              <div className="flex flex-col items-start gap-1">
                <p className="text-[11px] text-slate-500">
                  Press Esc to snooze all for 10 minutes.
                </p>
                <HideUntilLoginButton onClick={hideUntilLogin} />
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  pendingActions.forEach(n => snoozeActionNotification(n.id, 10));
                }}
                data-testid="snooze-all"
              >
                Snooze all
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="px-6 pt-5 pb-2 sr-only">
              <DialogTitle>{currentPagerItem?.title}</DialogTitle>
              <DialogDescription>{currentPagerItem?.message}</DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-3">
              {currentPagerItem && (
                <NotificationCard
                  notification={currentPagerItem}
                  onTakeAction={handleTakeAction}
                  onDismiss={handleDismiss}
                />
              )}
              {pendingActions.length > 1 && (
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={() => setPagerIdx(i => Math.max(0, i - 1))}
                    disabled={pagerIdx === 0}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="action-prev"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    Previous
                  </button>
                  <span className="text-[11px] text-slate-500">
                    {pagerIdx + 1} of {pendingActions.length}
                    {remainingCount > 0 ? ` · ${remainingCount} more pending` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPagerIdx(i => Math.min(pendingActions.length - 1, i + 1))}
                    disabled={pagerIdx >= pendingActions.length - 1}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="action-next"
                  >
                    Next
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              )}
              <p className="text-[10px] text-slate-400 text-center pt-1">
                Press Esc to snooze for 10 minutes.
              </p>
              <div className="text-center">
                <HideUntilLoginButton onClick={hideUntilLogin} />
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
